import {
  useId,
  useMemo,
  useState,
  Fragment,
} from 'react';
import type {
  MouseEvent,
  ChangeEvent,
} from 'react';
import classNames from 'classnames';
import {
  useAlerts,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  UserEntity,
  ImageEntity,
} from '@dinosave/core/types';
import {
  imageToBase64,
} from '@dinosave/core/utils';
import CropImageModal from '@/components/Modals/CropImageModal';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import {
  EditIcon,
} from '@/ui/icons';

import styles from './UserImage.module.css';

type UserImageProps = {
  user: UserEntity;
  withUpload?: boolean;
  withNavigation?: boolean;
  roundness?: 'round' | 'default';
}

const UserImage = (props: UserImageProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    user,
    withUpload = false,
    withNavigation = false,
    roundness = 'default',
  } = props;

  let id = useId();
  if (
    !withUpload
  ) id = null;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const updateAuthenticatedUser = useAuthentication(s => s.updateAuthenticatedUser);

  const addSuccess = useAlerts(s => s.addSuccess);

  const cropImageModal = useModal();

  // ##########################################################################
  // STATE
  // ##########################################################################

  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState('');

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const image = await imageToBase64(e.target.files[0]);
    setImage(image);
    cropImageModal.toggle();
  };

  const handleImageUpload = async (image: string) => {
    setLoading(true);

    try {
      const updatedUser = await updateAuthenticatedUser({
        ...authenticatedUser,
        image,
      });
      const userImage = updatedUser.image as ImageEntity;
      addSuccess({
        message: 'Image updated',
        image: userImage.small.url,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileClear = (e: MouseEvent<HTMLInputElement>) => e.currentTarget.value = null;

  // ##########################################################################
  // MEMOIZATION
  // ##########################################################################

  const imageUrl = useMemo(() => {
    const userId = user?.id || 0;
    const placeholder = `/images/avatars/avatar-${userId % 7}.svg`;
    const image = user?.image as ImageEntity;
    return image?.medium?.url || placeholder;
  }, [user]);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const userImageClassName = classNames({
    [styles.userImage]: true,
    [styles.round]: roundness === 'round',
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <CropImageModal
        {...cropImageModal}
        title="Edit image"
        image={image}
        aspect={1}
        maxHeight="25rem"
        cropShape="round"
        onCrop={handleImageUpload}
      />

      {withUpload && (
        <div className={userImageClassName}>
          <label
            htmlFor={id}
            className={styles.label}
          >
            <div className={styles.edit}>
              <Button
                color="secondary"
                roundness="round"
                centered
              >
                <label htmlFor={id}>
                  <EditIcon />
                </label>
              </Button>
            </div>

            <img
              alt="User"
              src={imageUrl}
              className={styles.image}
            />

            <input
              id={id}
              type="file"
              onChange={handleImageChange}
              onClick={handleFileClear}
              accept="image/png, image/jpeg, image/jpg"
            />
          </label>
        </div>
      )}

      {withNavigation && (
        <a
          href={`/u/${user?.slug}`}
          className={userImageClassName}
        >
          <img
            alt="User"
            src={imageUrl}
            className={styles.image}
          />
        </a>
      )}

      {!withUpload && !withNavigation && (
        <div className={userImageClassName}>
          <img
            alt="User"
            src={imageUrl}
            className={styles.image}
          />
        </div>
      )}
    </Fragment>
  );
};

export default UserImage;
